import { Component } from "react"
import { createPortal } from "react-dom"
import confetti from "canvas-confetti"
import EventAdaptingStateHandler from "src/states/EventAdaptingStateHandler"
import StateConstants from "src/states/StateContstants"
import ApiHandler from "src/api/ApiHandler"
import PlanContent from "./PlanContent"
import "./PlanModal.css"


interface PlanModalProps {
    stateHandler: EventAdaptingStateHandler
    apiHandler: ApiHandler
}

interface PlanModalState {
    isOpen: boolean
    showSuccess: boolean
}


class PlanModal extends Component<PlanModalProps, PlanModalState> {
    constructor(props: PlanModalProps) {
        super(props)

        this.state = {
            isOpen: false,
            showSuccess: false,
        }

        props.stateHandler.addListener(StateConstants.SHOW_PLAN_MODAL, (isOpen: boolean) => {
            if (isOpen) {
                const hasPending = !!(
                    props.stateHandler.get(StateConstants.PENDING_PAYMENT_INTENT_ID)
                    || localStorage.getItem("pendingPaymentIntentId")
                )
                if (hasPending) {
                    props.stateHandler.set(StateConstants.SHOW_PLAN_MODAL, false)
                    props.stateHandler.set(StateConstants.SHOW_LOGIN_MODAL, true)
                    return
                }
            }
            this.setState({ isOpen, showSuccess: false })
            document.body.style.overflow = isOpen ? "hidden" : ""
            document.documentElement.style.overflow = isOpen ? "hidden" : ""
        })
    }

    private confettiCanvas: HTMLCanvasElement | null = null

    showSuccess = () => {
        this.setState({ showSuccess: true })
        this.fireConfetti()
        this.refreshUserStatus()
    }

    async refreshUserStatus() {
        const maxAttempts = 5
        const delayMs = 2000

        for (let i = 0; i < maxAttempts; i++) {
            await new Promise(resolve => setTimeout(resolve, delayMs))
            this.props.apiHandler.clearCache()
            try {
                const userInfo = await this.props.apiHandler.getUserInfo()
                const features: string[] = userInfo?.features || []
                if (features.length > 0) {
                    this.props.stateHandler.set(StateConstants.USER_FEATURES, features)
                    this.props.stateHandler.set(StateConstants.ACCOUNT_TYPE, userInfo?.at || "basic")
                    this.props.stateHandler.set(StateConstants.IS_PREMIUM, true)
                    return
                }
            } catch (error) {
                // Retry
            }
        }
    }

    fireConfetti() {
        if (sessionStorage.getItem("confettiShown")) return
        sessionStorage.setItem("confettiShown", "true")

        if (!this.confettiCanvas) {
            this.confettiCanvas = document.createElement("canvas")
            this.confettiCanvas.style.position = "fixed"
            this.confettiCanvas.style.top = "0"
            this.confettiCanvas.style.left = "0"
            this.confettiCanvas.style.width = "100%"
            this.confettiCanvas.style.height = "100%"
            this.confettiCanvas.style.pointerEvents = "none"
            this.confettiCanvas.style.zIndex = "999999"
            document.body.appendChild(this.confettiCanvas)
        }

        const myConfetti = confetti.create(this.confettiCanvas, { resize: true })
        const duration = 3000
        const end = Date.now() + duration
        const frame = () => {
            myConfetti({ particleCount: 3, angle: 60, spread: 55, origin: { x: 0, y: 0.7 } })
            myConfetti({ particleCount: 3, angle: 120, spread: 55, origin: { x: 1, y: 0.7 } })
            if (Date.now() < end) {
                requestAnimationFrame(frame)
            } else {
                setTimeout(() => {
                    if (this.confettiCanvas) {
                        document.body.removeChild(this.confettiCanvas)
                        this.confettiCanvas = null
                    }
                }, 2000)
            }
        }
        frame()
    }

    close = () => {
        this.setState({ isOpen: false, showSuccess: false })
        document.body.style.overflow = ""
        document.documentElement.style.overflow = ""
    }

    render() {
        if (!this.state.isOpen) return null

        return createPortal(
            <>
                <div className="plan-modal-backdrop" onClick={this.close} />
                <div className="plan-modal">
                    <button className="plan-modal-close" onClick={this.close}>&#10005;</button>
                    <div className="plan-modal-body">
                        <PlanContent
                            stateHandler={this.props.stateHandler}
                            apiHandler={this.props.apiHandler}
                            showSuccess={this.state.showSuccess}
                            onPaymentComplete={this.showSuccess}
                        />
                    </div>
                </div>
            </>,
            document.body
        )
    }
}

export default PlanModal
